import React, { Fragment, Component, startTransition } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get, isEmpty, isEqual, debounce } from 'lodash-es';
import { Input } from 'reactstrap';
/* Constants */
import { CAR_BUYING_EVENTS } from 'site-modules/shared/constants/car-buying/car-buying-events';
import * as CHART from 'client/site-modules/shared/components/car-buying/offer-type-table/offer-type-table-chart-data';
import { CHART_PROPS_LEAD_FORM } from 'site-modules/shared/constants/price-checker';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
/* Components */
import { OfferTypeTableChartPrice } from 'site-modules/shared/components/car-buying/offer-type-table/offer-type-table-chart-price';
import { PriceCheckerChartLeadForm } from 'site-modules/shared/components/price-checker/price-checker-chart/price-checker-chart-lead-form';
/* Utils */
import { EventToolbox } from 'client/utils/event-toolbox';
import { formatPriceString } from 'site-modules/shared/utils/price-utils';
import {
  getSliderClosestValue,
  getSliderPayments,
} from 'site-modules/shared/components/car-buying/offer-type-table/offer-type-table-chart-utils';
import { getVDPPriceData } from 'client/site-modules/shared/utils/price-checker';

import './offer-type-table-price-chart.scss';

const SLIDER_ANIMATION_DURATION = 2000;
const EMPTY_STATE_TOGGLE_ANIMATION_DURATION = 600;
const SLIDE_ME = 'Slide Me';

export class OfferTypeTablePriceChart extends Component {
  static propTypes = {
    dealItems: PropTypes.shape({ [PropTypes.string]: PropTypes.arrayOf(PropTypes.shape({})) }),
    tmvData: VehicleEntities.NewTmv,
    selectedDealType: PropTypes.string.isRequired,
    onSliderMove: PropTypes.func.isRequired,
    isMobile: PropTypes.bool,
    isUsed: PropTypes.bool,
    isPurchase: PropTypes.bool,
    skipWidgetViewTracking: PropTypes.bool,
    noDealCheckAnimation: PropTypes.bool,
    dealerPrice: PropTypes.number,
    sellingPrice: PropTypes.number,
    showLeasePayments: PropTypes.bool,
    isLeasePaymentEnabled: PropTypes.bool,
    transactionCount: PropTypes.shape({
      tier1: PropTypes.number,
      tier2: PropTypes.number,
      tier3: PropTypes.number,
      tier4: PropTypes.number,
    }),
    forceLease: PropTypes.bool,
    creativeId: PropTypes.string,
  };

  static defaultProps = {
    dealItems: {},
    tmvData: null,
    isMobile: false,
    isUsed: false,
    isPurchase: false,
    skipWidgetViewTracking: false,
    noDealCheckAnimation: false,
    dealerPrice: null,
    sellingPrice: null,
    showLeasePayments: false,
    isLeasePaymentEnabled: false,
    transactionCount: null,
    forceLease: false,
    creativeId: undefined,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const updatedData = getSliderPayments({
      dealItems: nextProps.dealItems,
      isUsed: nextProps.isUsed,
      sellingPrice: nextProps.sellingPrice,
      showLeasePayments: nextProps.showLeasePayments,
      useBase: nextProps.showLeasePayments || nextProps.isLeasePaymentEnabled,
    });

    if (!updatedData) return null;

    return isEqual(updatedData.sliderPayments, prevState.sliderPayments) &&
      isEqual(updatedData.sliderLeasePayments, prevState.sliderLeasePayments) &&
      isEqual(updatedData.sliderMonthlyPayments, prevState.sliderMonthlyPayments) &&
      isEqual(updatedData.sliderLeaseMonthlyPayments, prevState.sliderLeaseMonthlyPayments) &&
      updatedData.sellingPrice === prevState.sellingPrice
      ? null
      : updatedData;
  }

  state = {
    sliderPayments: [],
    sliderLeasePayments: [],
    sliderMonthlyPayments: [],
    sliderLeaseMonthlyPayments: [],
    inputValue: this.props.sellingPrice || '',
    sellingPrice: this.props.sellingPrice,
    sliderValue: 0,
    lowerPayment: 0,
    upperPayment: 0,
    lowerLeasePayment: 0,
    upperLeasePayment: 0,
  };

  componentDidMount() {
    const { skipWidgetViewTracking } = this.props;

    if (!skipWidgetViewTracking) {
      const pageHeight = Math.max(document.body.scrollHeight, document.body.offsetHeight);
      EventToolbox.fireCustomEvent(CAR_BUYING_EVENTS.DEAL_CHECK_CORE_ENTRY, { pageHeight });
    }
    // subscribe chart to change the input value if it comes somewhere outside
    EventToolbox.on(CHART.CHART_VALUE_CHANGED_OUTSIDE, this.handleValueChangeOutside);

    this.setLeftStyle();
    this.addWindowResizeListener();
  }

  componentDidUpdate(prevProps, prevState) {
    const { inputValue, sliderValue } = this.state;
    const { noDealCheckAnimation, onSliderMove, selectedDealType, tmvData, sellingPrice, showLeasePayments } =
      this.props;

    const { sliderPayments, sliderMonthlyPayments } = this.getPayments(this.state);

    const hasSliderPayments = !isEmpty(sliderPayments);

    // enable slider animation for the first load
    if (!noDealCheckAnimation && hasSliderPayments && !this.sliderAnimated) {
      this.sliderAnimated = true;
      this.animateSlider();
    }

    const { lowerPayment } = this.getLowerUpperPayments(this.state);
    const { lowerPayment: prevLowerPayment } = this.getLowerUpperPayments(prevState);
    // restore slider empty state if payments recalculated
    if (prevLowerPayment && lowerPayment !== prevLowerPayment) {
      this.toggleSliderEmptyState();
    }

    if (
      showLeasePayments !== prevProps.showLeasePayments ||
      (!!sellingPrice && sellingPrice !== prevProps.sellingPrice) ||
      (hasSliderPayments && !isEqual(sliderMonthlyPayments, this.getPayments(prevState).sliderMonthlyPayments))
    ) {
      this.fireValueChangeEvent(get(sliderPayments, `[${sliderValue}]`, ''));
      this.setLeftStyle();
    }

    const isSliderValueUpdated = !prevState.sliderValue && !!sliderValue;

    if (!selectedDealType && isSliderValueUpdated && inputValue) {
      onSliderMove({
        dealType: getVDPPriceData({ ...tmvData, userDealerPrice: inputValue }).dealType.toLowerCase(),
        inputValue,
      });
    }
  }

  componentWillUnmount() {
    if (this.emptyStateRemovalTimeout) clearTimeout(this.emptyStateRemovalTimeout);
    if (this.animateTimeout) clearTimeout(this.animateTimeout);

    EventToolbox.off(CHART.CHART_VALUE_CHANGED_OUTSIDE, this.handleValueChangeOutside);

    this.removeWindowResizeListener();
  }

  /**
   * Returns ref for slider input wrapper.
   * @return {Element}
   */
  getSliderInputWrapperRef = () => this.chartInputsRef.current.querySelector('.slider-price-wrapper');

  /**
   * Get payments based on `isPurchase` prop.
   * @return {object}
   */
  getPayments = state => {
    const { isPurchase } = this.props;
    const { sliderPayments, sliderLeasePayments, sliderMonthlyPayments, sliderLeaseMonthlyPayments } = state;

    return {
      sliderPayments: isPurchase ? sliderPayments : sliderLeasePayments,
      sliderMonthlyPayments: isPurchase ? sliderMonthlyPayments : sliderLeaseMonthlyPayments,
    };
  };

  /**
   * Gets lower and upper limits for passed state based on `isPurchase` prop.
   * @param {object} state
   * @return {object}
   */
  getLowerUpperPayments = state => {
    const { isPurchase, showLeasePayments } = this.props;
    const { lowerPayment, upperPayment, lowerLeasePayment, upperLeasePayment, sliderLeaseMonthlyPayments } = state;

    if (showLeasePayments) {
      return {
        lowerPayment: sliderLeaseMonthlyPayments[0],
        upperPayment: sliderLeaseMonthlyPayments[sliderLeaseMonthlyPayments.length - 1],
      };
    }

    return {
      lowerPayment: isPurchase ? lowerPayment : lowerLeasePayment,
      upperPayment: isPurchase ? upperPayment : upperLeasePayment,
    };
  };

  /**
   * Gets position of the slider input based on slider position.
   * @returns {number}
   */
  getInputPosition = () => {
    const { sliderPayments, sliderValue } = this.state;
    const { isMobile } = this.props;

    const inputWidth = get(this.chartInputsRef, 'current.offsetWidth');
    const sliderWidth = get(this.chartSliderRef, 'current.offsetWidth');

    let position = 0;

    if (inputWidth && sliderWidth) {
      position = ((inputWidth - sliderWidth) / (sliderPayments.length - 1)) * sliderValue;
      if (isMobile) {
        position = ((inputWidth - sliderWidth) / 2 / (sliderPayments.length - 1)) * sliderValue;
      }
    }

    return position;
  };

  setLeftStyle = () => {
    const sliderInputWrapperLeftStyle = this.getInputPosition();
    this.setState({ sliderInputWrapperLeftStyle });
  };

  addWindowResizeListener = () => {
    if (this.props.isMobile) return;
    window.addEventListener('resize', this.windowResizeDebounced, false);
  };

  removeWindowResizeListener = () => {
    if (this.props.isMobile) return;
    window.removeEventListener('resize', this.windowResizeDebounced, false);
  };

  windowResizeDebounced = debounce(() => this.handleWindowResize(), 100);

  handleWindowResize = () => {
    startTransition(() => {
      this.setLeftStyle();
    });
  };

  /**
   * Fires slider move event.
   */
  fireSliderMoveEvent = debounce(
    actionCause =>
      EventToolbox.fireCustomEvent(CAR_BUYING_EVENTS.SLIDER_MOVE, { actionCause, creativeId: this.props.creativeId }),
    500
  );

  /**
   * Fires value change event.
   */
  fireValueChangeEvent = debounce(
    value =>
      EventToolbox.fireCustomEvent(CHART.CHART_VALUE_CHANGED, {
        value,
        loanMonthlyValue: value ? this.state.sliderMonthlyPayments[this.state.sliderValue] : '',
        leaseMonthlyValue: value ? this.state.sliderLeaseMonthlyPayments[this.state.sliderValue] : '',
      }),
    500
  );

  /**
   * Handles slider value change.
   * @param {Object} e
   */
  handleSliderChange = e => this.handleSliderMove(Number(e.target.value));

  /**
   * Handles value change if it comes outside.
   * @param {object} detail
   */
  handleValueChangeOutside = ({ detail }) => {
    this.handleSliderChange({
      target: { value: getSliderClosestValue(this.getPayments(this.state).sliderPayments, detail.value) },
    });
  };

  /**
   * Toggles slider input empty state.
   */
  toggleSliderEmptyState = () => {
    const element = this.getSliderInputWrapperRef();
    const isEmptyState = element.classList.contains('empty');
    const isFocused = element.classList.contains('focused');
    const isEmptyValue = this.state.inputValue === '';

    if (isEmptyState && (!isEmptyValue || isFocused)) {
      element.classList.remove('empty');
      element.classList.add('empty-out');
      this.emptyStateRemovalTimeout = setTimeout(() => {
        element.classList.remove('empty-out');
      }, EMPTY_STATE_TOGGLE_ANIMATION_DURATION);
      return;
    }

    if (isEmptyValue && !isEmptyState) {
      element.classList.add('empty');
      element.classList.remove('empty-out');
    }
  };

  /**
   * Handles animation of slider.
   */
  animateSlider = () => {
    this.chartInputsRef.current.classList.add('animate-slider');
    this.animateTimeout = setTimeout(() => {
      this.chartInputsRef.current.classList.remove('animate-slider');
      this.getSliderInputWrapperRef().classList.remove('hidden');
    }, SLIDER_ANIMATION_DURATION);
  };

  /**
   *  Handle moving of the slider based on slider value
   * @param {number} sliderValue
   */
  handleSliderMove = sliderValue => {
    const { sliderPayments, sliderMonthlyPayments } = this.getPayments(this.state);
    const { showLeasePayments, tmvData, onSliderMove } = this.props;
    const sliderInputWrapperLeftStyle = this.getInputPosition();

    const newInputValue = (showLeasePayments ? sliderMonthlyPayments : sliderPayments)[sliderValue] || '';
    this.setState({ sliderValue, inputValue: newInputValue, sliderInputWrapperLeftStyle }, () => {
      this.toggleSliderEmptyState();
      this.setLeftStyle();
    });

    this.fireSliderMoveEvent('drag');
    this.fireValueChangeEvent(get(sliderPayments, `[${sliderValue}]`, ''));
    onSliderMove({
      dealType: getVDPPriceData({ ...tmvData, userDealerPrice: newInputValue }).dealType.toLowerCase(),
      inputValue: newInputValue,
    });
  };

  chartInputsRef = React.createRef();
  chartSliderRef = React.createRef();

  animateTimeout = null;
  emptyStateRemovalTimeout = null;

  render() {
    const { isMobile, noDealCheckAnimation, dealerPrice, tmvData, showLeasePayments, transactionCount, forceLease } =
      this.props;

    const { inputValue, sliderValue, sliderInputWrapperLeftStyle } = this.state;
    const { columnWidth, columnPadding } = CHART_PROPS_LEAD_FORM.COMMON;
    const dataPoints = CHART_PROPS_LEAD_FORM.DATA_POINTS.WITH_DATA;
    const device = isMobile ? 'mobile' : 'desktop';
    const currentColumnWidth = columnWidth[device];
    const chartWidth = currentColumnWidth * dataPoints.length + columnPadding * (dataPoints.length - 1);
    const { lowerPayment, upperPayment } = this.getLowerUpperPayments(this.state);
    const ariaValuetext = inputValue ? `${formatPriceString(inputValue)}${showLeasePayments ? '/mo.' : ''}` : SLIDE_ME;

    return (
      <Fragment>
        <div className="offer-type-table-chart price-check d-inline-block pos-r">
          <div className="pos-r">
            <PriceCheckerChartLeadForm
              userDealerPrice={Number(inputValue)}
              tmvData={tmvData}
              lowerPayment={lowerPayment}
              upperPayment={upperPayment}
              isMobile={isMobile}
              dealerPrice={dealerPrice}
              showLeasePayments={showLeasePayments}
              currentTransactionCount={transactionCount}
              forceLease={forceLease}
            />
            <div className="pos-a w-100 d-flex justify-content-center left-0 vdp-chart-input-wrapper">
              <Input
                id="graphRangeInput"
                type="range"
                min={0}
                max={CHART.OFFER_TYPE_TABLE_CHART_POINTS.DATA_LENGTH}
                value={sliderValue}
                onChange={this.handleSliderChange}
                className="invisible-chart-input"
                aria-label="Enter payment"
                aria-valuetext={ariaValuetext}
              />
            </div>
          </div>
          <div
            className="chart-inputs pos-r mx-auto"
            style={{ maxWidth: `${chartWidth}px` }}
            data-tracking-parent="slider"
            ref={this.chartInputsRef}
            data-testid="slider"
          >
            <Input
              id="sliderRangeInput"
              type="range"
              min={0}
              max={CHART.OFFER_TYPE_TABLE_CHART_POINTS.DATA_LENGTH}
              value={sliderValue}
              onChange={this.handleSliderChange}
              className="pos-r py-1"
              aria-label="Enter payment"
              aria-valuetext={ariaValuetext}
            />
            <div className="slider-range-colors w-100 rounded" />
            {isMobile ? (
              <div
                style={{ left: sliderInputWrapperLeftStyle }}
                className={classNames('slider-price-wrapper ed-zdepth-3 empty slider-input-mobile', {
                  hidden: !noDealCheckAnimation,
                  'no-animation': noDealCheckAnimation,
                  invisible: noDealCheckAnimation && sliderInputWrapperLeftStyle === 0 && !inputValue,
                })}
              >
                <div
                  style={{ left: sliderInputWrapperLeftStyle }}
                  className="slider-control ed-zdepth-3 bg-white d-flex justify-content-center align-items-center pos-a"
                  ref={this.chartSliderRef}
                >
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            ) : (
              <div
                style={{ left: sliderInputWrapperLeftStyle }}
                className={classNames('slider-price-wrapper ed-zdepth-3 bg-white', {
                  empty: !inputValue,
                  hidden: !noDealCheckAnimation,
                  'no-animation': noDealCheckAnimation,
                  invisible: noDealCheckAnimation && sliderInputWrapperLeftStyle === 0 && !inputValue,
                })}
                ref={this.chartSliderRef}
              >
                <OfferTypeTableChartPrice clearValue={inputValue} showLeasePayments={showLeasePayments} />

                <span className="slide-me-placeholder small text-gray-darker h-100 w-100 d-flex justify-content-between align-items-center px-0_5">
                  <span className="icon-arrow-left3 xsmall" aria-hidden />
                  {SLIDE_ME}
                  <span className="icon-arrow-right3 xsmall" aria-hidden />
                </span>
              </div>
            )}
          </div>
        </div>
      </Fragment>
    );
  }
}
